import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Download, Loader2, FileBarChart2 } from "lucide-react";
import { toast } from "sonner";
import { REPORTS } from "@/constants/testIds";

export default function ReportsListPage() {
  const [reports, setReports] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamId, setTeamId] = useState("all");
  const [month, setMonth] = useState("all");
  const [reportType, setReportType] = useState("all");
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    const params = {};
    if (teamId !== "all") params.team_id = teamId;
    if (month !== "all") params.month = month;
    if (reportType !== "all") params.report_type = reportType;
    const r = await api.get("/reports", { params });
    setReports(r.data);
    setMonths([...new Set(r.data.map((x) => x.month))].sort().reverse());
    setLoading(false);
  };

  useEffect(() => { api.get("/teams").then((r) => setTeams(r.data)); }, []);
  useEffect(() => { load(); }, [teamId, month, reportType]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    if (!window.confirm("Delete this report and all its rows?")) return;
    await api.delete(`/reports/${id}`);
    toast.success("Report deleted");
    load();
  };

  const download = async (id, filename) => {
    const r = await api.get(`/reports/${id}/download`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "report.xlsx";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit font-semibold text-3xl md:text-4xl text-slate-900 tracking-tight">Reports</h1>
          <p className="text-sm text-slate-600 mt-1">All monthly reactivation reports per team.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Select value={teamId} onValueChange={setTeamId}>
            <SelectTrigger className="w-[160px] glass-input" data-testid={REPORTS.filterTeam}>
              <SelectValue placeholder="Team" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teams</SelectItem>
              {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-[140px] glass-input" data-testid={REPORTS.filterMonth}>
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All months</SelectItem>
              {months.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={reportType} onValueChange={setReportType}>
            <SelectTrigger className="w-[160px] glass-input" data-testid={REPORTS.filterReportType}>
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="reactivation">Reactivation</SelectItem>
              <SelectItem value="ftd">FTD</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => nav("/generate")} className="btn-tactile" style={{ background: "linear-gradient(135deg,#0A84FF,#0069cc)" }}>
            Daily upload
          </Button>
        </div>
      </div>

      <div className="glass-card p-4">
        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : reports.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            <FileBarChart2 className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            No reports yet. Upload a monthly Excel to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-500 text-xs uppercase tracking-wide">
                  <th className="text-left py-2 px-3">Team</th>
                  <th className="text-left py-2 px-3">Month</th>
                  <th className="text-left py-2 px-3">Type</th>
                  <th className="text-left py-2 px-3">Rows</th>
                  <th className="text-left py-2 px-3">Source</th>
                  <th className="text-left py-2 px-3">Uploaded by</th>
                  <th className="text-left py-2 px-3">When</th>
                  <th className="text-right py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((r) => (
                  <tr key={r.id} className="border-t border-white/60 hover:bg-white/50">
                    <td className="py-2 px-3 font-medium text-slate-900">{r.team_name}</td>
                    <td className="py-2 px-3">{r.month}</td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${r.report_type === "ftd" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
                        {(r.report_type || "reactivation").toUpperCase()}
                      </span>
                    </td>
                    <td className="py-2 px-3">{r.row_count}</td>
                    <td className="py-2 px-3">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{r.source}</span>
                    </td>
                    <td className="py-2 px-3 text-slate-600 text-xs">{r.uploaded_by}</td>
                    <td className="py-2 px-3 text-slate-500 text-xs">{new Date(r.uploaded_at).toLocaleString()}</td>
                    <td className="py-2 px-3 text-right">
                      <Link to={`/reports/${r.id}`} className="text-[#0A84FF] mr-3">Open</Link>
                      <button onClick={() => download(r.id, r.filename)} className="text-slate-600 mr-3 inline-flex items-center gap-1">
                        <Download className="w-3 h-3" />
                      </button>
                      <button onClick={() => remove(r.id)} className="text-red-500">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
